import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface OccupationSalaryData {
  soc: string;
  mean_salary?: number;
  median_salary?: number;
  hourly_mean?: number;
  total_employment?: number;
}

export const useOccupationSalaryData = (socCodes: string[], areaCode: string = '99') => {
  return useQuery<OccupationSalaryData[]>({
    queryKey: ['occupation-salary-data', socCodes, areaCode],
    queryFn: async (): Promise<OccupationSalaryData[]> => {
      if (!socCodes || socCodes.length === 0) {
        return [];
      }

      // Fetch salary data for each SOC code using the API function
      const results = await Promise.allSettled(
        socCodes.map(async (soc) => {
          try {
            const { data, error } = await supabase.functions.invoke('api-salary', {
              body: {
                area: areaCode === '99' ? 'United States' : 'United States',
                soc: soc,
                rpp: false
              }
            });
            
            if (error) throw error;
            
            return {
              soc,
              mean_salary: data?.annual_mean_wage || data?.mean_salary || 0,
              median_salary: data?.annual_median_wage || data?.median_salary || 0,
              hourly_mean: data?.hourly_mean_wage || data?.hourly_mean || 0,
              total_employment: data?.total_employment || data?.tot_emp || 0
            };
          } catch (error) {
            console.error(`Error fetching salary for SOC ${soc}:`, error);
            // Return empty data structure instead of throwing to allow partial results
            return { 
              soc,
              mean_salary: undefined,
              median_salary: undefined,
              hourly_mean: undefined,
              total_employment: undefined
            };
          }
        })
      );

      return results.map(result => 
        result.status === 'fulfilled' ? result.value : { soc: 'unknown' }
      );
    },
    staleTime: 1000 * 60 * 60 * 2, // 2 hours cache
    gcTime: 1000 * 60 * 60 * 4, // 4 hours
    enabled: socCodes.length > 0,
  });
};